import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, Observable } from 'rxjs';

export interface ToasterConfig {
  id?: string;
  type: 'success' | 'info' | 'warning' | 'error';
  title: string;
  message: string;
  duration?: number; // milliseconds
  icon?: string; // Tabler icon name (e.g., 'tablerBell')
  clickAction?: () => void; // Optional click handler
  notificationData?: any; // Optional notification details
}

export interface Toaster extends ToasterConfig {
  id: string;
}

@Injectable({
  providedIn: 'root'
})
export class ToasterService {
  private toasterSubject = new Subject<Toaster>();
  private clearSubject = new Subject<void>();
  private toasterArray: Toaster[] = [];
  private recentNotificationIds = new Set<string>(); // Track recently shown notifications to prevent duplicates
  private readonly DEDUP_WINDOW_MS = 10000; // 10 second deduplication window

  constructor(private router: Router) { }

  /**
   * Get toaster observable for components to subscribe
   */
  getToasters(): Observable<Toaster> {
    return this.toasterSubject.asObservable();
  }

  /**
   * Signal for the container to drop every toaster it is currently rendering.
   */
  getClearSignal(): Observable<void> {
    return this.clearSubject.asObservable();
  }

  /**
   * Show a toaster notification
   */
  show(config: ToasterConfig): string {
    // Nothing may be announced to a logged-out visitor. Both logout paths wipe
    // localStorage before routing to /auth/login, so a missing userId is the
    // "no session" marker. This is the last line of defence for toasters that
    // are raised late: fetchAndShowLatestNotification() staggers its batch with
    // setTimeout(index * 300) and the stream socket can still deliver a frame
    // while the session-expired dialog counts down, so those fire AFTER the
    // navigation and used to land on top of the login screen.
    // `sessionID` is the key AuthGuard itself checks: a guard-driven bounce to
    // /auth/login leaves the rest of localStorage intact, so the userId test
    // alone still let a toaster land on the login form.
    if (!localStorage.getItem('userId') || !localStorage.getItem('sessionID')) {
      return '';
    }

    // Belt and braces: never announce anything while an auth screen is showing
    // (e.g. signed out in another tab, so this tab's keys are still present).
    if (this.router.url.startsWith('/auth')) {
      return '';
    }

    // Generate deduplication key from notification data or title+message
    const dedupKey = this.generateDedupKey(config);

    // Check for duplicate within deduplication window
    if (dedupKey && this.recentNotificationIds.has(dedupKey)) {
      console.log('Duplicate notification suppressed:', dedupKey);
      return ''; // Return empty string to indicate suppressed
    }

    const toaster: Toaster = {
      id: config.id || this.generateId(),
      type: config.type || 'info',
      title: config.title,
      message: config.message,
      duration: config.duration || 5000,
      icon: config.icon || this.getDefaultIcon(config.type),
      clickAction: config.clickAction,
      notificationData: config.notificationData
    };

    // Track this notification to prevent duplicates
    if (dedupKey) {
      this.recentNotificationIds.add(dedupKey);
      // Remove from tracking after dedup window expires. Keys built from a
      // notification record's id stay until clear() (logout): one notification
      // toasts at most once per session, whichever nav-bar instance raises it.
      if (!dedupKey.startsWith('notif-')) {
        setTimeout(() => {
          this.recentNotificationIds.delete(dedupKey);
        }, this.DEDUP_WINDOW_MS);
      }
    }

    this.toasterArray.push(toaster);
    this.toasterSubject.next(toaster);

    return toaster.id;
  }

  /**
   * Generate deduplication key from notification config
   */
  private generateDedupKey(config: ToasterConfig): string | null {
    // If notification has specific data with ID, use that
    if (config.notificationData) {
      const data = config.notificationData;
      // Try various ID fields that might be present.
      // `notifyId` first: that is the real key on the backend notification record
      // (Notification.java / notification-mappers.ts). It was missing here, so
      // id-based de-duplication never matched and every notification fell through
      // to the weaker jobId / title+message fallbacks below.
      if (data.notifyId) return `notif-${data.notifyId}`;
      if (data.notificationId) return `notif-${data.notificationId}`;
      if (data.id) return `notif-${data.id}`;
      if (data.notifyData?.jobId) return `job-${data.notifyData.jobId}-${config.title}`;
      if (data.notifyData?.candidateId) return `candidate-${data.notifyData.candidateId}-${config.title}`;
    }
    // Fallback to title + message hash for stream notifications
    return `${config.type}-${config.title}-${config.message}`;
  }

  /**
   * Show success toaster
   */
  success(title: string, message: string, duration?: number): string {
    return this.show({
      type: 'success',
      title,
      message,
      duration
    });
  }

  /**
   * Show info toaster
   */
  info(title: string, message: string, duration?: number): string {
    return this.show({
      type: 'info',
      title,
      message,
      duration
    });
  }

  /**
   * Show warning toaster
   */
  warning(title: string, message: string, duration?: number): string {
    return this.show({
      type: 'warning',
      title,
      message,
      duration
    });
  }

  /**
   * Show error toaster
   */
  error(title: string, message: string, duration?: number): string {
    return this.show({
      type: 'error',
      title,
      message,
      duration
    });
  }

  /**
   * Show notification from stream data
   */
  showStreamNotification(notificationType: string, count: number): void {
    let title = '';
    let message = '';
    let type: 'success' | 'info' | 'warning' | 'error' = 'info';

    switch (notificationType) {
      case 'jobApplicationsReceived':
        title = 'New Application';
        message = `You have ${count} new job application${count > 1 ? 's' : ''}`;
        type = 'success';
        break;
      case 'jobInvitesReceived':
        title = 'New Invite';
        message = `You have ${count} new job invite${count > 1 ? 's' : ''}`;
        type = 'info';
        break;
      // Toasters removed for message/chat actions (2026-09-12). Both call sites in
      // langding-page-nav-bar.component.ts are commented out, so these cases are
      // unreachable; kept here commented so the behaviour can be restored together.
      // case 'jobNonSeenMessage':
      //   title = 'New Message';
      //   message = `You have ${count} unread job message${count > 1 ? 's' : ''}`;
      //   type = 'info';
      //   break;
      // case 'candidateNonSeenMessage':
      //   title = 'New Message';
      //   message = `You have ${count} unread candidate message${count > 1 ? 's' : ''}`;
      //   type = 'info';
      //   break;
      default:
        title = 'Notification';
        message = 'You have a new notification';
        type = 'info';
    }

    this.show({
      type,
      title,
      message,
      duration: 5000
    });
  }

  /**
   * Remove toaster by ID
   */
  remove(id: string): void {
    this.toasterArray = this.toasterArray.filter(t => t.id !== id);
  }

  /**
   * Clear all toasters.
   *
   * The container keeps its own `toasters` list and lives in app.component.html
   * OUTSIDE the router outlet, so it survives every navigation. Emptying only
   * this service's array left the visible ones on screen; the clear signal is
   * what actually removes them. Also drops the de-dup ledger so the next
   * session is not silenced by keys the previous user generated.
   */
  clear(): void {
    this.toasterArray = [];
    this.recentNotificationIds.clear();
    this.clearSubject.next();
  }

  /**
   * Generate unique ID
   */
  private generateId(): string {
    return `toaster-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get default icon based on type
   */
  private getDefaultIcon(type: string): string {
    const iconMap = {
      success: 'tablerCircleCheck',
      info: 'tablerInfoCircle',
      warning: 'tablerAlertTriangle',
      error: 'tablerCircleX'
    };
    return iconMap[type] || 'tablerBell';
  }
}
